import React from 'react';
import styled from 'styled-components';
import IconBlock from '@component/common/block/icon-block';
import TextBlock from '@component/common/block/text-block';
import Line from '@component/common/line';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme, TIcon, TIconSize } from '@datatype/type';

type TComponentSize = 'small' | 'medium' | 'large';
export type TCanvasToolbarItem = 'lock' | 'divider' | 'zoom' | 'fit' | 'move' | 'rotate' | 'memo' | 'save';
export type TCanvasToolbarAction = Exclude<TCanvasToolbarItem, 'divider' | 'zoom'> | 'zoom-out' | 'zoom-in';

interface IProps {
  items: readonly TCanvasToolbarItem[];
  zoomRatio?: number;
  moveActive?: boolean;
  theme?: ETheme;
  size?: TComponentSize;
  width?: '100%' | 'fit-content' | string;
  height?: '100%' | 'auto' | string;
  border?: boolean;
  handleClick?: (action: TCanvasToolbarAction) => void;
}

const createDesign = (
  componentSize: TComponentSize | undefined,
  theme: ETheme | undefined,
  width: string | undefined,
  height: string | undefined,
  border: boolean,
) => {
  // ############### createSize ###############
  const createSize = () => {
    switch (componentSize) {
      case 'small':
        return {
          padding: size.DESIGN_SYSTEM_BUTTON_SMALL_PADDING_X,
          gap: size.MARGIN_1,
          iconSize: 'tiny' as TIconSize,
          textSize: 'small' as const,
          dividerHeight: size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT,
        };
      case 'large':
        return {
          padding: size.DESIGN_SYSTEM_BUTTON_LARGE_PADDING_X,
          gap: size.MARGIN_2,
          iconSize: 'medium' as TIconSize,
          textSize: 'large' as const,
          dividerHeight: size.DESIGN_SYSTEM_BUTTON_LARGE_HEIGHT,
        };
      case 'medium':
      default:
        return {
          padding: size.DESIGN_SYSTEM_BUTTON_MEDIUM_PADDING_X,
          gap: size.MARGIN_1,
          iconSize: 'small' as TIconSize,
          textSize: 'medium' as const,
          dividerHeight: size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT,
        };
    }
  };

  // ############### createTheme ###############
  const createTheme = () => {
    switch (theme) {
      case ETheme.CYAN:
        return { iconColor: color.CYAN_1, textColor: color.CYAN_1, borderColor: color.CYAN_4 };
      case ETheme.GRAY:
        return { iconColor: color.BLACK_2, textColor: color.BLACK_2, borderColor: color.GRAY_2 };
      case ETheme.BROWN:
      default:
        return { iconColor: color.BROWN_1, textColor: color.BROWN_1, borderColor: color.BROWN_3 };
    }
  };

  // ############### createEvent ###############
  const createEvent = () => ({ cursor: 'pointer' as const });

  // ############### createCustom ###############
  const createCustom = () => ({
    backgroundColor: border ? color.WHITE_1 : 'transparent',
    borderRadius: border ? size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS : '0px',
    borderWidth: border ? size.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH : '0px',
    shadow: border ? color.SURFACE_SHADOW : 'none',
  });

  const sizeDesign = createSize();
  const themeDesign = createTheme();
  const customDesign = createCustom();

  return {
    width,
    height,
    ...sizeDesign,
    ...themeDesign,
    ...customDesign,
    padding: border ? sizeDesign.padding : '0px',
    event: createEvent(),
  };
};

interface IWrapperProps {
  $design: ReturnType<typeof createDesign>;
}

const Wrapper = styled.div<IWrapperProps>`
  display: flex;
  width: ${(props) => props.$design.width || 'fit-content'};
  height: ${(props) => props.$design.height || 'auto'};
  padding: ${(props) => props.$design.padding};
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.$design.gap};
  box-sizing: border-box;
  background-color: ${(props) => props.$design.backgroundColor};
  border: ${(props) => props.$design.borderWidth} solid ${(props) => props.$design.borderColor};
  border-radius: ${(props) => props.$design.borderRadius};
  box-shadow: ${(props) => props.$design.shadow};
`;

const ZoomContainer = styled.div<IWrapperProps>`
  display: flex;
  align-items: center;
  gap: ${(props) => props.$design.gap};
`;

const ITEM_ICON: Record<Exclude<TCanvasToolbarItem, 'divider' | 'zoom'>, TIcon> = {
  lock: 'lock',
  fit: 'scan',
  rotate: 'rotate-right',
  move: 'move',
  memo: 'memo-text',
  save: 'save',
};

const CanvasToolbar: React.FC<IProps> = ({
  items,
  zoomRatio,
  moveActive = false,
  theme,
  size: componentSize,
  width,
  height,
  border = true,
  handleClick,
}) => {
  const design = createDesign(componentSize, theme, width, height, border);
  const iconDesign = {
    box: { event: design.event },
    icon: { size: design.iconSize, kind: 'outlined' as const, color: design.iconColor },
  };

  return (
    <Wrapper $design={design}>
      {items.map((item, index) => {
        if (item === 'divider') {
          return (
            <Line
              key={`${item}-${index}`}
              direction='vertical'
              height={design.dividerHeight}
              color={design.borderColor}
            />
          );
        }
        if (item === 'zoom') {
          return (
            <ZoomContainer key={item} $design={design}>
              <IconBlock icon='zoom-out' design={iconDesign} click={{ handler: () => handleClick?.('zoom-out') }} />
              <TextBlock
                text={`${zoomRatio ?? 100}%`}
                design={{ text: { size: design.textSize, color: design.textColor, bold: true } }}
              />
              <IconBlock icon='zoom-in' design={iconDesign} click={{ handler: () => handleClick?.('zoom-in') }} />
            </ZoomContainer>
          );
        }
        return (
          <IconBlock
            key={item}
            icon={ITEM_ICON[item]}
            design={
              item === 'move'
                ? {
                    ...iconDesign,
                    box: {
                      ...iconDesign.box,
                      width: '28px',
                      height: '28px',
                      borderRadius: size.BORDER_RADIUS_1,
                      backgroundColor: moveActive ? color.GRAY_3 : 'transparent',
                    },
                    icon: { ...iconDesign.icon, color: moveActive ? color.BLACK_2 : color.GRAY_1 },
                  }
                : iconDesign
            }
            click={{ handler: () => handleClick?.(item) }}
          />
        );
      })}
    </Wrapper>
  );
};

export default CanvasToolbar;
